import * as styles from './StarRatingInput.css';

import { type ComponentProps, type FC, useState } from 'react';

import { Text, Box, IconStar } from 'braid-design-system';
import TextButton from '../TextButton';
import translations from './.vocab';
import { useTranslations } from '@vocab/react';

interface Props {
  onClick: (x: number) => number;
  rating: number;
  animated: boolean;
  idPrefix: string;
  fullName: string;
  // FIX ME - these fields are there because to support the alignment in animation, revisit when animation is removed
  detailedView: boolean;
  itemJustifyContent?: ComponentProps<typeof Box>['justifyContent'];
  itemWrapperPaddingX?: ComponentProps<typeof Box>['paddingX'];
}

export const StarRatingInput: FC<Props> = ({
  onClick,
  animated,
  detailedView,
  rating,
  idPrefix,
  fullName,
  itemJustifyContent = 'flexStart',
  itemWrapperPaddingX = undefined,
}) => {
  const [currentHoverIndex, setCurrentHoverIndex] = useState(0);
  const { t } = useTranslations(translations);
  const onStarClick = (ratingValue: number) => {
    const newRatingValue = onClick(ratingValue);
    setCurrentHoverIndex(newRatingValue);
  };

  const isIE =
    navigator.userAgent.indexOf('MSIE') !== -1 ||
    navigator.appVersion.indexOf('Trident/') > -1;

  const showAnimation = animated && rating === 0 && !isIE;

  return (
    <Box
      className={{
        [styles.animated]: showAnimation,
      }}
      display="flex"
      alignItems="center"
      justifyContent="flexStart"
    >
      <Box className={styles.outer}>
        <Box className={styles.inner}>
          <Box
            className={styles.starRatingInput}
            paddingX={itemWrapperPaddingX}
          >
            <Box
              display="flex"
              className={{
                [styles.detailedSpacing]: detailedView,
                [styles.listSpacing]: !detailedView,
              }}
            >
              {[...Array(5)].map((e, i) => {
                const index = i + 1;
                return (
                  <Box
                    key={`star-rating-${index}`}
                    display="flex"
                    justifyContent={itemJustifyContent}
                    alignItems="center"
                    className={styles.starRatingInputOption}
                    onMouseEnter={() => setCurrentHoverIndex(index)}
                    onMouseLeave={() => setCurrentHoverIndex(0)}
                  >
                    <Text>
                      <Box className={styles.star}>
                        <TextButton
                          aria-label={t('Star rating', { index })}
                          data-testid={`rating_${index}`}
                          onClick={() => onStarClick(index)}
                        >
                          <IconStar
                            active={
                              rating >= index || currentHoverIndex >= index
                            }
                            title={`${fullName} ${t(
                              'Star rating image',
                            )} ${index}`}
                            titleId={`${idPrefix}-starRatingSvg${index}`}
                          />
                        </TextButton>
                      </Box>
                    </Text>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
